import { Component, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { AppService } from '../../app.service';
import { NotificationService } from '../../core/services/notification.service';
import { NotificationType } from '../../core/services/notification-type';
import { KeyValueObject } from '../../shared/models/core/KeyValueObject';
import { LeadDetails } from '../../shared/models/lead/lead-details';
import { LookupService } from '../../shared/services/lookup.service';
import { LeadsService } from '../../shared/services/leads.service';
import { LeadArchiveDialogComponent } from '../lead-archive-dialog/lead-archive-dialog.component';
import { LeadPostponeDialogComponent } from '../lead-postpone-dialog/lead-postpone-dialog.component';
import { applyLeadDialogTitle } from '../lead-dialog-title';
import { hasUtmTags } from '../../shared/utils/utm-tag-parser';

@Component({
	selector: 'lead-detail-dialog',
	templateUrl: './lead-detail-dialog.component.html',
	styleUrls: ['./lead-detail-dialog.component.scss'],
})
export class LeadDetailDialogComponent implements OnInit, OnDestroy {
	@Input() refreshList: () => void = () => {};
	readonly hasUtmTags = hasUtmTags;
	private readonly leadUpdatedStorageEventKey = 'lead-updated-event';
	private readonly notProcessedLeadStatusId = 1;
	private readonly postponedLeadStatusId = 2;
	private readonly archivedLeadStatusId = 3;
	private readonly dealLeadStatusId = 4;
	private readonly leadInitTypeIds = [1, 2, 6];

	selectedLead: LeadDetails = null;
	leadLoading = false;
	leadLoadError = false;
	commentText = '';
	initTypeEditing = false;
	initTypeInput = '';
	initTypeDraft = '';
	initTypeSaving = false;
	initTypeOptionsLoading = false;
	initTypePopupSettings = { height: 105, width: 'auto' };
	initTypeDropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'contains' };
	allInitTypeOptions: KeyValueObject[] = [];
	serviceCenters: KeyValueObject[] = [];
	draftServiceCenterId: number = null;
	serviceCenterSaving = false;
	serviceCenterDropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'contains' };

	private _leadId: number;
	private readonly leadUpdatedStorageHandler = (event: StorageEvent) => this.onLeadUpdatedStorageEvent(event);

	constructor(
		private injector: Injector,
		private appService: AppService,
		private dataService: LeadsService,
		private lookupService: LookupService,
		private dialogService: DialogService,
		private notificationService: NotificationService,
		private router: Router
	) {}

	ngOnInit(): void {
		window.addEventListener('storage', this.leadUpdatedStorageHandler);
		this.loadServiceCenters();
	}

	ngOnDestroy(): void {
		window.removeEventListener('storage', this.leadUpdatedStorageHandler);
	}

	@Input()
	set leadId(v: number) {
		this._leadId = v;
		if (v != null && v > 0) {
			this.loadLeadDetails(v);
		}
	}

	get leadId(): number {
		return this._leadId;
	}

	get showCreateRequestButton(): boolean {
		return !!this.selectedLead;
	}

	get showPostponeButton(): boolean {
		if (!this.selectedLead) {
			return false;
		}

		return this.selectedLead.statusId !== this.postponedLeadStatusId
			&& this.selectedLead.statusId !== this.archivedLeadStatusId
			&& this.selectedLead.statusId !== this.dealLeadStatusId;
	}

	get showArchiveButton(): boolean {
		if (!this.selectedLead) {
			return false;
		}

		return this.selectedLead.statusId === this.notProcessedLeadStatusId
			|| this.selectedLead.statusId === this.postponedLeadStatusId;
	}

	get showInitTypeEditButton(): boolean {
		const formName = this.selectedLead && this.selectedLead.formName;
		return !!this.selectedLead
			&& !!formName
			&& !!formName.trim()
			&& this.selectedLead.hasFormMapping === false;
	}

	get showInitTypeReadonly(): boolean {
		return !!this.selectedLead && !this.showInitTypeEditButton;
	}

	statusBadgeStyle(): { [key: string]: string } | null {
		if (!this.selectedLead) {
			return null;
		}
		const bg = this.selectedLead.statusBlockColor != null ? String(this.selectedLead.statusBlockColor).trim() : '';
		const fg = this.selectedLead.statusTextColor != null ? String(this.selectedLead.statusTextColor).trim() : '';
		if (!bg && !fg) {
			return null;
		}
		const s: { [key: string]: string } = {};
		if (bg) {
			s['background-color'] = bg;
		}
		if (fg) {
			s['color'] = fg;
		}
		return s;
	}

	emptyDash(value: string | null | undefined): string {
		if (value === null || value === undefined) {
			return '\u2014';
		}
		const t = String(value).trim();
		return t.length ? value : '\u2014';
	}

	closeDialog(): void {
		if (this.refreshList) {
			this.refreshList();
		}
		this.injector.get(DialogRef).close();
	}

	addComment(): void {
		if (!this.selectedLead || !this.commentText || !this.commentText.trim()) {
			return;
		}

		this.dataService.addComment(this.selectedLead.leadId, this.commentText.trim()).subscribe(x => {
			if (x.isSuccessful) {
				this.commentText = '';
				this.loadLeadDetails(this.selectedLead.leadId);
			}
		});
	}

	linkContragent(): void {
		if (!this.selectedLead || !this.selectedLead.suggestedContragent || !this.selectedLead.suggestedContragent.contragentId) {
			return;
		}

		this.dataService.linkContragent(this.selectedLead.leadId, this.selectedLead.suggestedContragent.contragentId).subscribe(x => {
			if (x.isSuccessful) {
				this.loadLeadDetails(this.selectedLead.leadId);
				if (this.refreshList) {
					this.refreshList();
				}
			}
		});
	}

	openPostponeModal(): void {
		if (!this.selectedLead) {
			return;
		}
		const w = this.appService.isMobileDevice ? '95%' : '440px';
		const dialogRef = this.dialogService.open({
			content: LeadPostponeDialogComponent,
			title: 'Отложить лид',
			width: w,
			height: '320px',
		});
		const c = dialogRef.content.instance as LeadPostponeDialogComponent;
		c.leadId = this.selectedLead.leadId;
		c.onSuccess = () => {
			this.loadLeadDetails(this.selectedLead.leadId);
			if (this.refreshList) {
				this.refreshList();
			}
		};
	}

	openNewRequestInNewTab(): void {
		if (!this.selectedLead) {
			return;
		}
		const url = this.router.serializeUrl(
			this.router.createUrlTree(['/requests/new'], { queryParams: { leadId: this.selectedLead.leadId } })
		);
		window.open(url, '_blank', 'noopener,noreferrer');
	}

	openArchiveModal(): void {
		if (!this.selectedLead) {
			return;
		}
		const w = this.appService.isMobileDevice ? '95%' : '440px';
		const dialogRef = this.dialogService.open({
			content: LeadArchiveDialogComponent,
			title: 'Архивирование лида',
			width: w,
			height: '400px',
		});
		const c = dialogRef.content.instance as LeadArchiveDialogComponent;
		c.leadId = this.selectedLead.leadId;
		c.onSuccess = () => {
			this.loadLeadDetails(this.selectedLead.leadId);
			if (this.refreshList) {
				this.refreshList();
			}
		};
	}

	startInitTypeEdit(): void {
		if (!this.selectedLead || !this.showInitTypeEditButton) {
			return;
		}
		this.initTypeEditing = true;
		this.initTypeInput = '';
		this.initTypeDraft = '';
		this.loadInitTypeOptions();
	}

	cancelInitTypeEdit(): void {
		this.initTypeEditing = false;
		this.initTypeInput = '';
		this.initTypeDraft = '';
		this.allInitTypeOptions = [];
	}

	onInitTypeFilterChange(value: string): void {
		if (!this.initTypeEditing) {
			return;
		}
		this.initTypeDraft = value || '';
	}

	get initTypeCommitValue(): string {
		return (this.initTypeInput || this.initTypeDraft || '').trim();
	}

	get canSaveInitType(): boolean {
		return !this.initTypeSaving && this.initTypeCommitValue.length > 0;
	}

	onInitTypeComboOpen(): void {
		if (!this.initTypeEditing) {
			return;
		}
		this.loadInitTypeOptions();
	}

	onInitTypeSelectionChange(value: KeyValueObject): void {
		if (!this.initTypeEditing || this.initTypeSaving || !value) {
			return;
		}

		const trimmed = (value.name || '').trim();
		if (!trimmed) {
			return;
		}

		this.initTypeInput = trimmed;
		this.initTypeDraft = trimmed;
		this.saveInitType(value.id, null, false);
	}

	commitInitTypeEdit(event?: Event): void {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		if (!this.selectedLead || !this.initTypeEditing || this.initTypeSaving) {
			return;
		}

		const trimmed = this.initTypeCommitValue;
		if (!trimmed) {
			return;
		}

		this.initTypeInput = trimmed;
		this.initTypeDraft = trimmed;

		const existing = this.findInitTypeOption(trimmed);
		if (!existing) {
			this.notificationService.error({
				title: 'Ошибка',
				message: 'Выберите источник из списка: Ручное заведение, Сайт или OpenVillage',
				notificationType: NotificationType.Toast
			});
			return;
		}

		this.saveInitType(existing.id, null, false);
	}

	get canSaveServiceCenter(): boolean {
		return !!this.selectedLead
			&& !this.serviceCenterSaving
			&& this.draftServiceCenterId != null
			&& this.draftServiceCenterId !== this.selectedLead.serviceCenterId;
	}

	onServiceCenterDraftChange(serviceCenterId: number): void {
		this.draftServiceCenterId = serviceCenterId;
	}

	saveServiceCenter(): void {
		if (!this.canSaveServiceCenter) {
			return;
		}

		const selected = this.serviceCenters.find(x => x.id === this.draftServiceCenterId);
		if (selected && selected.requiresTransferWarning) {
			this.notificationService.confirmation({
				title: '',
				message: 'После сохранения лид будет передан другому СЦ и станет вам недоступен',
				confirmButtonText: 'OK',
				cancelButtonText: 'Отмена',
				type: 'warning'
			}, () => {
				this.persistServiceCenter(true);
			}, () => {
				this.draftServiceCenterId = this.selectedLead ? this.selectedLead.serviceCenterId : null;
			});
			return;
		}

		this.persistServiceCenter(false);
	}

	private persistServiceCenter(closeAfterSave: boolean): void {
		if (!this.selectedLead || this.draftServiceCenterId == null) {
			return;
		}

		this.serviceCenterSaving = true;
		this.dataService.assignServiceCenter(this.selectedLead.leadId, this.draftServiceCenterId).subscribe(
			updated => {
				this.serviceCenterSaving = false;
				this.selectedLead.serviceCenterId = updated.serviceCenterId;
				this.selectedLead.serviceCenterName = updated.serviceCenterName;
				this.draftServiceCenterId = updated.serviceCenterId;
				if (this.refreshList) {
					this.refreshList();
				}
				if (closeAfterSave) {
					this.closeDialog();
				}
			},
			err => {
				this.serviceCenterSaving = false;
				this.draftServiceCenterId = this.selectedLead.serviceCenterId;
				const message = (err && err.message) || 'Не удалось сохранить сервисный центр';
				this.notificationService.error({
					title: 'Ошибка',
					message: message,
					notificationType: NotificationType.Toast
				});
			}
		);
	}

	private loadServiceCenters(): void {
		this.dataService.getAssignableServiceCenters().subscribe(
			items => {
				this.serviceCenters = items || [];
				this.ensureCurrentServiceCenterInList();
			},
			() => {
				this.serviceCenters = [];
			}
		);
	}

	private ensureCurrentServiceCenterInList(): void {
		if (!this.selectedLead || this.selectedLead.serviceCenterId == null) {
			return;
		}

		if (this.serviceCenters.some(x => x.id === this.selectedLead.serviceCenterId)) {
			return;
		}

		this.serviceCenters = [
			...this.serviceCenters,
			new KeyValueObject(
				this.selectedLead.serviceCenterId,
				this.selectedLead.serviceCenterName || ('СЦ #' + this.selectedLead.serviceCenterId)
			)
		];
	}

	private loadInitTypeOptions(): void {
		if (this.allInitTypeOptions.length > 0 || this.initTypeOptionsLoading) {
			return;
		}

		this.initTypeOptionsLoading = true;
		this.lookupService.getDataForCombobox('init-types', null, { isError: false }).subscribe(
			items => {
				this.allInitTypeOptions = (items || []).filter(x => this.leadInitTypeIds.includes(x.id));
				this.initTypeOptionsLoading = false;
			},
			() => {
				this.initTypeOptionsLoading = false;
				this.allInitTypeOptions = [];
			}
		);
	}

	private findInitTypeOption(name: string): KeyValueObject | null {
		const trimmed = (name || '').trim();
		if (!trimmed) {
			return null;
		}

		const lower = trimmed.toLowerCase();
		return this.allInitTypeOptions.find(x => x.name && x.name.trim().toLowerCase() === lower) || null;
	}

	private saveInitType(initTypeId: number | null, initTypeName: string | null, isNewInitType: boolean = false): void {
		if (!this.selectedLead) {
			return;
		}

		if (!this.selectedLead.formName || !this.selectedLead.formName.trim()) {
			this.notificationService.error({
				title: 'Ошибка',
				message: 'Невозможно привязать источник: у лида не указано наименование формы.',
				notificationType: NotificationType.SweetAlert
			});
			return;
		}

		this.initTypeSaving = true;
		this.dataService.assignInitType(this.selectedLead.leadId, initTypeId, initTypeName).subscribe(
			updated => {
				this.initTypeSaving = false;
				this.initTypeEditing = false;
				this.initTypeInput = '';
				this.initTypeDraft = '';
				this.notificationService.success({
					title: '',
					message: isNewInitType ? 'Источник создан и привязан к форме' : 'Источник сохранён',
					notificationType: NotificationType.Toast
				});
				if (updated) {
					this.selectedLead.initTypeId = updated.initTypeId;
					this.selectedLead.initTypeName = updated.initTypeName;
					this.selectedLead.hasFormMapping = updated.hasFormMapping;
					this.selectedLead.defaultRequestTypeId = updated.defaultRequestTypeId;
					this.selectedLead.defaultRequestCategoryId = updated.defaultRequestCategoryId;
					if (updated.initTypeId != null && updated.initTypeName) {
						const existsInCache = this.allInitTypeOptions.some(x => x.id === updated.initTypeId);
						if (!existsInCache) {
							this.allInitTypeOptions.push(new KeyValueObject(updated.initTypeId, updated.initTypeName));
						}
					}
				} else {
					this.loadLeadDetails(this.selectedLead.leadId);
				}
				if (this.refreshList) {
					this.refreshList();
				}
			},
			err => {
				this.initTypeSaving = false;
				const message = (err && err.message) || 'Не удалось сохранить источник';
				this.notificationService.error({
					title: 'Ошибка',
					message: message,
					notificationType: NotificationType.Toast
				});
			}
		);
	}

	private loadLeadDetails(leadId: number): void {
		this.leadLoading = true;
		this.dataService.getLeadById(leadId).subscribe(
			data => {
				this.selectedLead = data;
				this.initTypeEditing = false;
				this.initTypeInput = '';
				this.initTypeDraft = '';
				this.allInitTypeOptions = [];
				this.leadLoading = false;
				this.leadLoadError = false;
				this.draftServiceCenterId = data.serviceCenterId;
				this.ensureCurrentServiceCenterInList();
				applyLeadDialogTitle(this.injector.get(DialogRef, null), {
					formName: data.formName,
					description: data.description,
					rawExtraData: data.rawExtraData,
				});
			},
			() => {
				this.leadLoading = false;
				this.leadLoadError = true;
			}
		);
	}

	private onLeadUpdatedStorageEvent(event: StorageEvent): void {
		if (event.key !== this.leadUpdatedStorageEventKey || !event.newValue || !this.leadId) {
			return;
		}

		try {
			const payload = JSON.parse(event.newValue) as { leadId?: number };
			if (payload && payload.leadId === this.leadId) {
				this.loadLeadDetails(this.leadId);
				if (this.refreshList) {
					this.refreshList();
				}
			}
		} catch {
			// ignore invalid cross-tab payload
		}
	}
}
